// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { supabase, authHelpers } from '../lib/supabase'
import { withTimeout, TimeoutError } from '../lib/supabaseClient'
import { showError } from '../lib/toast'
import posthog from 'posthog-js'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false) // Track profile fetch separately
  const [profileError, setProfileError] = useState(null) // Track profile fetch errors

  // Use ref to track in-flight profile fetch to prevent race conditions
  const profileFetchInProgress = useRef(false)
  const abortControllerRef = useRef(null)
  // Track which user ID we've already loaded a profile for.
  // Using a ref (not state) because the onAuthStateChange callback captures
  // a stale closure — reading `profile?.id` there always sees the initial null.
  const profileLoadedForRef = useRef(null)

  // Guard: only update user state when the user ID actually changes.
  // Prevents downstream cascades (PlanContext, dashboard effects) on
  // Supabase token refreshes that fire SIGNED_IN with the same user.
  const userIdRef = useRef(null)
  const updateUser = useCallback((newUser) => {
    if (newUser?.id === userIdRef.current) return
    userIdRef.current = newUser?.id ?? null
    setUser(newUser)
  }, [])

  useEffect(() => {
    // Skip if supabase is not configured
    if (!supabase) {
      setLoading(false)
      return
    }

    // Check active session
    checkUser()

    // Listen for auth changes
    const { data: { subscription } } = authHelpers.onAuthStateChange(
      async (event, session) => {
        if (import.meta.env.VITE_DEBUG) {
          console.log('Auth event:', event)
        }

        // Handle different auth events
        if (event === 'SIGNED_IN') {
          if (session?.user) {
            updateUser(session.user)
            posthog.opt_in_capturing()
            posthog.identify(session.user.id)
            // Only fetch profile if not already in progress
            // Don't await - let it happen in background
            if (!profileFetchInProgress.current) {
              fetchProfile(session.user.id)
            }
          }
          setLoading(false)
        } else if (event === 'SIGNED_OUT') {
          posthog.opt_out_capturing()
          posthog.reset()
          updateUser(null)
          setProfile(null)
          profileLoadedForRef.current = null
          setLoading(false)
        } else if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
          // No-op: loading is already false by this point, and calling
          // setLoading(false) triggers an unnecessary re-render cascade
          // through DashboardRouter
        } else if (event === 'INITIAL_SESSION') {
          // This event fires on page load if session exists
          // checkUser() already handles this, so ignore
          if (import.meta.env.VITE_DEBUG) {
            console.log('INITIAL_SESSION event (already handled by checkUser)')
          }
        }
      }
    )

    // Cleanup
    return () => {
      subscription.unsubscribe()
      // Abort any in-progress fetch
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const checkUser = async () => {
    try {
      const session = await authHelpers.getSession()
      if (session?.user) {
        updateUser(session.user)
        // Don't await - let profile fetch happen in background
        // This allows the app to render immediately while profile loads
        fetchProfile(session.user.id)
      }
    } catch (error) {
      console.error('Error checking user:', error)
      showError('Failed to load session. Please refresh the page.')
    } finally {
      setLoading(false)
    }
  }

  const fetchProfile = async (userId) => {
    // Guard: skip fetch when no authenticated user
    if (!userId) return

    // Prevent duplicate simultaneous fetches
    if (profileFetchInProgress.current) {
      if (import.meta.env.VITE_DEBUG) {
        console.log('Profile fetch already in progress, skipping...')
      }
      return
    }

    // If we already have this profile, don't fetch again.
    // Uses a ref instead of profile?.id because the onAuthStateChange
    // callback captures a stale closure where profile is always null.
    if (profileLoadedForRef.current === userId) {
      if (import.meta.env.VITE_DEBUG) {
        console.log('Profile already loaded for this user')
      }
      return
    }

    try {
      profileFetchInProgress.current = true
      setProfileLoading(true) // Mark profile as loading

      // Create abort controller for this request
      abortControllerRef.current = new AbortController()

      // Fetch profile with simple timeout (no retry to avoid multiple errors)
      const fetchPromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId);

      const result = await withTimeout(fetchPromise, 15000)
      const { data: dataArray, error } = result
      const data = dataArray?.[0] || null

      // Check if aborted
      if (abortControllerRef.current?.signal.aborted) {
        return
      }

      if (error) {
        // Profile doesn't exist yet, create it
        if (error.code === 'PGRST116' || error.code === 'NOT_FOUND') {
          await createProfile(userId)
          setProfileError(null)
        } else {
          // For other errors, throw to catch block
          throw error
        }
      } else {
        setProfile(data)
        setProfileError(null)
        profileLoadedForRef.current = userId
      }
    } catch (error) {
      console.error('Error fetching profile:', error)

      // Check if it's a timeout
      if (error instanceof TimeoutError) {
        setProfileError({ type: 'timeout', message: 'Profile fetch timed out' })
        showError('Loading is taking longer than expected. Please refresh the page.')
        // Keep user logged in
      } else if (error.message?.includes('JWT') || error.message?.includes('auth')) {
        // Auth errors - sign user out
        showError('Session expired. Please sign in again.')
        setProfile(null)
        profileLoadedForRef.current = null
        setUser(null)
        setProfileError({ type: 'auth', message: 'Session expired' })
      } else {
        // Network/other errors - keep user logged in
        setProfileError({ type: 'network', message: error.message })
        showError('Failed to load profile. Please refresh the page.')
      }
    } finally {
      profileFetchInProgress.current = false
      setProfileLoading(false) // Mark profile as done loading
      abortControllerRef.current = null
    }
  }

  const createProfile = async (userId) => {
    try {
      // Get fresh user data from Supabase
      const { data: { user: currentUser } } = await supabase.auth.getUser()

      if (!currentUser) {
        console.error('No user found when creating profile')
        setProfile(null)
        return
      }

      const insertPromise = supabase
        .from('profiles')
        .insert([
          {
            id: userId,
            email: currentUser.email,
            role: 'customer', // Default role
            full_name: currentUser.user_metadata?.full_name || currentUser.user_metadata?.name || ''
          }
        ])
        .select()
        .single();

      const { data, error } = await withTimeout(insertPromise, 15000)

      if (error) {
        // If profile already exists (race condition), fetch it
        if (error.message?.includes('unique') || error.message?.includes('duplicate')) {
          await fetchProfile(userId)
          return
        }
        throw error
      }

      setProfile(data)
    } catch (error) {
      console.error('Error creating profile:', error)
      showError('Failed to create profile. Please try again.')
      setProfile(null)
    }
  }

  const signUp = async (email, password, role = 'customer', fullName = '', utmData = {}, options = {}) => {
    const { data, error } = await authHelpers.signUp(
      email,
      password,
      { role, full_name: fullName, ...utmData },
      options
    )
    return { data, error }
  }

  const signIn = async (email, password) => {
    const { data, error } = await authHelpers.signIn(email, password)
    return { data, error }
  }

  const signInWithGoogle = async (options = {}) => {
    const { data, error } = await authHelpers.signInWithGoogle(options)
    return { data, error }
  }

  const signOut = async () => {
    const { error } = await authHelpers.signOut()
    if (!error) {
      setUser(null)
      setProfile(null)
      profileLoadedForRef.current = null
    }
    return { error }
  }

  const updateProfile = async (updates) => {
    try {
      const updatePromise = supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      const { data, error } = await withTimeout(updatePromise, 15000)

      if (error) throw error
      setProfile(data)
      return { data, error: null }
    } catch (error) {
      console.error('Error updating profile:', error)
      return { data: null, error }
    }
  }

  const value = {
    user,
    profile,
    loading,
    profileLoading, // Separate loading state for profile
    profileError,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    updateProfile,
    retryFetchProfile: () => fetchProfile(user?.id) // Allow manual retry
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
